/**
 * PencilCard — the canonical warm-paper surface.
 *
 * Translucent paper over the cream page (``bg-white/55``) with a warm-tinted
 * shadow-as-border (the 2026 pencil "middle path": depth without a chunky
 * white block). Use for grouping overview KPIs, chart panels, stat blocks —
 * anywhere finance-hub-style containment reads better than a bare section.
 *
 * NOT for tables (those live directly on cream — see the ando-ui-review rule)
 * and NOT a replacement for HighlightNote (callouts) or a solid ``bg-card``
 * chunky card (the anti-pattern this exists to avoid).
 */
import type * as React from "react";

import { cn } from "../../lib/cn";

export interface PencilCardProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  /** Gain a warm orange ring + lift on hover (clickable cards). */
  readonly interactive?: boolean;
  readonly as?: "div" | "section";
}

export const PencilCard: React.FC<PencilCardProps> = ({
  children,
  className,
  interactive,
  as: Tag = "div",
}) => (
  <Tag
    className={cn(
      "rounded-2xl bg-white/55",
      "shadow-[0_0_0_1px_rgba(60,40,20,0.06),0_1px_2px_rgba(60,40,20,0.05)]",
      interactive &&
        "outline-none transition-all hover:-translate-y-0.5 hover:bg-white/75 hover:shadow-[0_0_0_1px_rgba(255,122,26,0.35),0_6px_18px_-6px_rgba(255,122,26,0.24)] focus-visible:ring-2 focus-visible:ring-ando-orange/40",
      className,
    )}
  >
    {children}
  </Tag>
);

PencilCard.displayName = "PencilCard";
